import { Client, UserCredentials } from '@botpress/webchat-client'
import { createContext, ReactNode, useContext, useMemo } from 'react'
import { type Events } from '../events'
import { type StorageLocation, type WebchatError } from '../types'
import { useInitialization } from '../hooks/useInitialization'
import { createEventEmitter } from '@bpinternal/shared'

type ClientStates = 'connecting' | 'connected' | 'error' | 'disconnected'

export type WebchatContextValue = {
  client: Client | undefined
  userCredentials: UserCredentials | undefined
  conversationId: string | undefined
  openConversation: (params: { conversationId?: string; userToken?: string }) => Promise<void>
  clientState: ClientStates
  setClientState: (state: ClientStates) => void
  error: WebchatError | undefined
  setError: (error?: WebchatError) => void
  isTyping: boolean
  isAwaitingResponse: boolean
  setAwaitingResponse: (state: boolean, timeout?: number) => void
  emitter: ReturnType<typeof createEventEmitter<Events>>
}

const WebchatContext = createContext<WebchatContextValue | null>(null)

export function useWebchatContext(): WebchatContextValue {
  const ctx = useContext(WebchatContext)
  if (!ctx) {
    throw new Error('useWebchatContext must be used inside <WebchatProvider>')
  }
  return ctx
}

type WebchatProviderProps = {
  clientId: string
  apiUrl?: string
  userCredentials?: UserCredentials
  conversationId?: string
  adminSecret?: string
  storageKey?: string
  storageLocation?: StorageLocation
  clientHeaders?: Record<string, string>
  children: ReactNode
}

export function WebchatProvider({
  clientId,
  apiUrl,
  storageKey,
  storageLocation,
  adminSecret,
  clientHeaders,
  children,
  ...props
}: WebchatProviderProps) {
  const {
    clientState,
    httpClient,
    error,
    activeConversationId,
    userCredentials,
    isTyping,
    isAwaitingResponse,
    openConversation,
    setClientState,
    setError,
    setAwaitingResponse,
    emitter,
  } = useInitialization({
    apiUrl,
    clientId,
    storageKey,
    storageLocation,
    adminSecret,
    clientHeaders,
    conversationId: props.conversationId,
    userToken: props.userCredentials?.userToken,
  })

  const contextValue: WebchatContextValue = {
    client: httpClient,
    clientState,
    setClientState,
    conversationId: activeConversationId,
    userCredentials,
    error,
    setError,
    isTyping,
    isAwaitingResponse,
    openConversation,
    setAwaitingResponse,
    emitter,
  }

  return <WebchatContext.Provider value={contextValue}>{children}</WebchatContext.Provider>
}
